import React, { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import IntegrationActivityLog from "../IntegrationActivityLog";
import { SectionCard, PrimaryButton, SecondaryButton, FormRow, Divider } from "@/components/ds";

interface SquarespaceConfigState {
  connected: boolean;
  id?: number;
  storeUrl?: string;
  displayName?: string;
  isEnabled?: boolean;
  hasApiKey?: boolean;
}

const SQUARESPACE_SKILLS = [
  { name: "squarespace_get_order", label: "Get order", description: "Look up a specific order by ID: status, total, tracking." },
  { name: "squarespace_search_orders", label: "Search orders", description: "Find orders by customer email or fulfillment status." },
  { name: "squarespace_get_customer_orders", label: "Get customer order history", description: "List a customer's full order history by email." },
  { name: "squarespace_search_products", label: "Search products", description: "Search the product catalog by name or SKU." },
  { name: "squarespace_get_product", label: "Get product", description: "Full details for a single product by ID." },
  { name: "squarespace_check_inventory", label: "Check inventory", description: "Live stock quantity for a product or SKU." },
  { name: "squarespace_get_store_info", label: "Get store info", description: "Confirms the connected store URL and connection status." }
];

export default function Squarespace() {
  const api = useApi();
  const [config, setConfig] = useState<SquarespaceConfigState>({ connected: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [storeUrl, setStoreUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [displayName, setDisplayName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await (await api.admin.get("Squarespace/Config")).json();
      setConfig(data);
      setStoreUrl(data.storeUrl || "");
      setDisplayName(data.displayName || "");
      setMode(data.connected ? "view" : "edit");
    } catch (err) {
      setError(`Failed to load Squarespace config: ${err instanceof Response ? (err as Response).status : String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (config.connected && config.id && !apiKey) {
        // Toggle-only save (store URL / display name), no key rotation. Server-side, the
        // PUT DTO never accepts an API Key field (ArchTest-enforced) so a key rotation always
        // has to go through POST /Squarespace/Config below instead.
        const resp = await (await api.admin.put(`Squarespace/Config/${config.id}`, { storeUrl, displayName })).json();
        if (!resp.success) setError(resp.message || "Failed to update Squarespace config.");
      } else if (config.connected && config.id && apiKey) {
        // Rotating the API key -- POST /Squarespace/Config is an upsert, so it goes through
        // the same validated path a fresh connect uses.
        const resp = await (await api.admin.post("Squarespace/Config", { storeUrl, apiKey, displayName })).json();
        if (!resp.success) setError(resp.message || "Failed to update Squarespace config.");
      } else {
        if (!apiKey && !config.connected) {
          setError("Commerce API Key is required.");
          setSaving(false);
          return;
        }
        // Re-connecting (or rotating the key) always goes through the create-or-update
        // endpoint, which validates the new key before persisting it.
        const resp = await (await api.admin.post("Squarespace/Config", { storeUrl, apiKey, displayName })).json();
        if (!resp.success) {
          setError(resp.message || "Failed to connect Squarespace.");
          setSaving(false);
          return;
        }
      }
      setApiKey("");
      await load();
      setMode("view");
    } catch (err) {
      setError(`Save failed: ${err instanceof Response ? (err as Response).status : String(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const resp = await (await api.admin.post("Squarespace/TestConnection", {})).json();
      setTestResult(resp.message || (resp.success ? "Connected." : "Connection failed."));
    } catch (err) {
      setTestResult(`Test failed: ${err instanceof Response ? (err as Response).status : String(err)}`);
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!config.id) return;
    if (!window.confirm("Disconnect Squarespace? Existing skills will stop working until reconnected.")) return;
    try {
      await api.admin.delete(`Squarespace/Config/${config.id}`);
      await load();
    } catch (err) {
      setError(`Disconnect failed: ${err instanceof Response ? (err as Response).status : String(err)}`);
    }
  };

  return (
    <div>
      <h1 className="mb-1 text-xl font-semibold text-zinc-800">Squarespace</h1>
      <p className="mb-4 text-sm text-zinc-600">Commerce order and product lookup for chat and voice.</p>

      {error && (
        <div role="alert" className="mb-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <SectionCard
        title="Connection"
        action={
          config.connected && mode === "view" ? (
            <div className="flex gap-2">
              <SecondaryButton onClick={() => setMode("edit")}>Edit</SecondaryButton>
              <SecondaryButton onClick={handleTest} disabled={testing}>
                {testing ? "Testing..." : "Test Connection"}
              </SecondaryButton>
            </div>
          ) : undefined
        }
      >
        {loading ? (
          <p className="text-sm text-zinc-600">Loading...</p>
        ) : mode === "view" && config.connected ? (
          <div className="space-y-2 text-sm">
            <p>
              <strong>Store URL:</strong> {config.storeUrl || "(not set)"}
            </p>
            <p>
              <strong>Status:</strong> {config.isEnabled ? "Active" : "Disabled"}
            </p>
            <p>
              <strong>API Key:</strong> {config.hasApiKey ? "On file" : "Missing"}
            </p>
            {testResult && <p className="text-zinc-700">{testResult}</p>}
            <Divider />
            <GhostDisconnect onClick={handleDisconnect} />
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">
              Generate a Commerce API Key in your Squarespace site: Settings, Advanced, API Keys.
              Grant it Orders, Inventory, and Products read permission. Squarespace has no cart or
              checkout API, this connection is for order, tracking, and product lookups only.
              Checkout always stays on your Squarespace-hosted pages.
            </p>
            <FormRow label="Store URL" hint="e.g. https://mystore.squarespace.com (optional, for reference only)">
              <input
                type="text"
                value={storeUrl}
                onChange={(e) => setStoreUrl(e.target.value)}
                className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"
                placeholder="https://mystore.squarespace.com"
              />
            </FormRow>
            <FormRow label="Commerce API Key" hint={config.connected ? "Leave blank to keep the existing key" : "Bearer token from Settings > Advanced > API Keys"}>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"
                placeholder={config.connected ? "Unchanged" : "Paste API key"}
                aria-label="Commerce API Key"
              />
            </FormRow>
            <FormRow label="Display name" hint="Optional label shown in admin UI">
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"
              />
            </FormRow>
            <div className="flex gap-2">
              <PrimaryButton onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : config.connected ? "Save changes" : "Connect Squarespace"}
              </PrimaryButton>
              {config.connected && <SecondaryButton onClick={() => setMode("view")}>Cancel</SecondaryButton>}
            </div>
          </div>
        )}
      </SectionCard>

      <div className="mt-6">
        <SectionCard title="AI Skills">
          <p className="mb-3 text-sm text-zinc-600">
            Read-only skills. Squarespace has no cart or checkout API, so the bot can look up
            orders, tracking, and products but can never place, modify, or complete a purchase
            in chat.
          </p>
          <ul className="space-y-2 text-sm">
            {SQUARESPACE_SKILLS.map((s) => (
              <li key={s.name}>
                <strong>{s.label}</strong>: {s.description}
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>

      <div className="mt-6">
        <IntegrationActivityLog integrationSlug="Squarespace" />
      </div>
    </div>
  );
}

function GhostDisconnect({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-sm text-red-600 underline hover:text-red-800"
    >
      Disconnect Squarespace
    </button>
  );
}
